import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  IconButton,
  Chip,
  Box,
  Divider,
  Typography,
  Avatar,
  Popover,
  Tooltip,
  SvgIcon,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  ChatBubbleOutlineOutlined,
  SettingsOutlined,
  CodeOutlined,
  CheckCircleOutlineOutlined,
  SmartToyOutlined,
  Build,
  School,
  ArrowDropDown,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import ProImg1 from "../../../../assets/images/AgentStar.svg";
import { WorkflowListItem } from "../../../../Services/WorkflowService";
import { formatDate } from "../../../utils/Common/Common";
import { useNavigate } from "react-router-dom";
import {
  StartLoading,
  StopLoading,
} from "../../../Store/LoadingReducer/LoaderActions";
import { KNOWLEDGE_BASE, TOOLS } from "../../../../Services/Endpoint";
import { useDispatch } from "react-redux";
import BaseInstance from "../../../../Services/AxiosInstance/BaseInstance";
import { promises } from "dns";
import { KnowledgeBase } from "../../../Types/KnowledgeBase";
import { ShowError } from "../../../Store/MessageReducer/MsgActions";
import Tool from "../../../Types/Tool";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Dialog, DialogTitle, DialogContent, Tabs, Tab } from "@mui/material";

import calendarIcon from "../../../../assets/images/calendarIcon.svg";
import CustomEmbedIcon from "../../../../assets/images/codeIcon.svg";
import Embed from "../../../../Views/Components/Embed";

interface WorkflowCardProps {
  workflow: WorkflowListItem;
  deleteFun: (workflow: WorkflowListItem) => void
  editFun: (workflow: WorkflowListItem) => void;
  hideUpdate?: boolean;
}

const WorkflowCard: React.FC<WorkflowCardProps> = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const [openEmbed, setOpenEmbed] = useState(false);

  // Settings dropdown state
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const settingsOpen = Boolean(settingsAnchorEl);

  // Description truncation detection
  const descriptionRef = React.useRef<HTMLSpanElement>(null);
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);

  useEffect(() => {
    const element = descriptionRef.current;
    if (element) {
      setIsDescriptionTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [props.workflow.description]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Settings dropdown handlers
  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleGraphicEdit = () => {
    setSettingsAnchorEl(null);
    navigate(`/workflows/edit/${props.workflow.id}`);
  };

  const handlePopupEdit = () => {
    setSettingsAnchorEl(null);
    props.editFun(props.workflow);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    props.editFun(props.workflow);
    handleMenuClose();
  };

  const handleDelete = () => {
    props.deleteFun(props.workflow);
    handleMenuClose();
  };

  return (
    <Card
      sx={{
        borderRadius: "12px",
        backgroundColor: "rgba(256, 256, 256, 0.7)",
        height: "100%",
        boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(5px)",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar
            src={ProImg1}
            sx={{
              backgroundColor: "#04DB93",
              "& img": {
                objectFit: "none",
              },
            }}
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                color: "#1a1a1a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {props.workflow.name}
            </Typography>
            <Tooltip
              title={isDescriptionTruncated ? props.workflow.description : ''}
              arrow
              placement="bottom"
              disableHoverListener={!isDescriptionTruncated}
            >
              <Typography
                ref={descriptionRef}
                variant="body2"
                sx={{
                  color: "#666",
                  fontSize: "12px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  mt: 0.5,
                }}
              >
                {props.workflow.description}
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <CheckCircleOutlineOutlined
              sx={{ fontSize: "16px", color: props.workflow.status === 'active' ? "#4caf50" : "#f44336" }}
            />
            <Typography variant="body2" sx={{ fontSize: "12px", color: "#666" }}>
              {props.workflow.status === 'active' ? "Active" : "Inactive"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <img src={calendarIcon} alt="Calendar" style={{ width: "16px", height: "16px" }} />
            <Typography variant="body2" sx={{ fontSize: "12px", color: "#666" }}>
              {props.workflow.created_at ? formatDate(props.workflow.created_at) : "N/A"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label={props.workflow.status}
              size="small"
              sx={{
                fontSize: "10px",
                height: "24px",
                backgroundColor: props.workflow.status === 'active' ? "#e8f5e8" : "#ffeaea",
                color: props.workflow.status === 'active' ? "#2e7d32" : "#d32f2f",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Edit Workflow">
              <IconButton onClick={() => navigate(`/workflows/edit/${props.workflow.id}`)} size="small">
                <SettingsOutlined fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Embed">
              <IconButton
                onClick={() => setOpenEmbed(true)}
                size="small"
                sx={{
                  width: "36px",
                }}
              >
                <img src={CustomEmbedIcon} alt="Embed" />
              </IconButton>
            </Tooltip>


            <Tooltip title="Delete Workflow">
              <IconButton
                onClick={() => props.deleteFun(props.workflow)}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Settings Menu removed - direct navigation on click */}
      </CardContent>

      {/* embed drawer */}
      {openEmbed && props.workflow.id && (
        <Embed
          setOpenEmbed={setOpenEmbed}
          openEmbed={openEmbed}
          workflow={props.workflow}
        />
      )}
    </Card>
  );
};

export default WorkflowCard;
